import { useGetSavedCryptoAddresses } from '@/entities/crypto-address';
import {
  ApiErrorText,
  Button,
  ModalComponentWrapper,
} from '@/shared/components';
import { useTranslation } from 'react-i18next';
import { SavedAddressesSkeleton } from './SavedAddressesSkeleton';
import { SavedAddressesEmptyState } from './SavedAddressesEmptyState';
import { SavedAddressesContent } from './SavedAddressesContent';
import { useSavedAddressesModal } from '../model';
import { useEditCryptoAddressModal } from '@/features/edit-crypto-address';
import type { SavedCryptoAddress } from '@/entities/crypto-address';
import {
  useGetSavedBankAddresses,
  type SavedBankAddress,
} from '@/entities/bank-address';
import { useCreateSavedAddressModal } from '@/widgets/create-saved-address-modal';
import { useEditBankAddressModal } from '@/features/edit-bank-address';
import { useGetSavedCards, type SavedCard } from '@/entities/card';
import { useEditCardModal } from '@/features/edit-card';

type Props = {
  onBack: () => void;
};

export function SavedAddressesModal({ onBack }: Props) {
  const { t } = useTranslation();

  const {
    data: cryptoAddresses,
    isLoading: isCryptoAddressesLoading,
    error: cryptoAddressesError,
  } = useGetSavedCryptoAddresses();

  const {
    data: bankAddresses,
    isLoading: isBankAddressesLoading,
    error: bankAddressesError,
  } = useGetSavedBankAddresses();

  const {
    data: cards,
    isLoading: isCardsLoading,
    error: cardsError,
  } = useGetSavedCards();

  const isLoading =
    isCryptoAddressesLoading || isBankAddressesLoading || isCardsLoading;
  const error = cryptoAddressesError || bankAddressesError || cardsError;

  const isEmpty =
    !isLoading &&
    !error &&
    cryptoAddresses?.length === 0 &&
    bankAddresses?.length === 0 &&
    cards?.length === 0;
  const hasData =
    !isLoading &&
    !error &&
    ((cryptoAddresses?.length ?? 0) > 0 ||
      (bankAddresses?.length ?? 0) > 0 ||
      (cards?.length ?? 0) > 0);

  const { openSavedAddressesModal } = useSavedAddressesModal();
  const { openCreateSavedAddressModal } = useCreateSavedAddressModal();

  const { openEditCryptoAddressModal } = useEditCryptoAddressModal();
  const { openEditBankAddressModal } = useEditBankAddressModal();
  const { openEditCardModal } = useEditCardModal();

  const createSavedAddress = () =>
    openCreateSavedAddressModal({
      onBack: () => openSavedAddressesModal({ onBack }),
    });

  const editCryptoAddress = (savedCryptoAddress: SavedCryptoAddress) =>
    openEditCryptoAddressModal({
      onBack: () => openSavedAddressesModal({ onBack }),
      savedCryptoAddress,
    });

  const editBankAddress = (savedBankAddress: SavedBankAddress) =>
    openEditBankAddressModal({
      onBack: () => openSavedAddressesModal({ onBack }),
      savedBankAddress,
    });

  const editCard = (savedCard: SavedCard) =>
    openEditCardModal({
      onBack: () => openSavedAddressesModal({ onBack }),
      savedCard,
    });

  return (
    <ModalComponentWrapper
      title={t('savedAddresses.title')}
      onBack={onBack}
      headerClassName="text-secondary-bg bg-secondary-bg/50"
      footer={
        <Button size="lg" onClick={createSavedAddress}>
          {t('savedAddresses.emptyState.buttonText')}
        </Button>
      }>
      {isLoading && <SavedAddressesSkeleton />}

      {error && <ApiErrorText error={error} />}

      {isEmpty && <SavedAddressesEmptyState />}

      {hasData && (
        <SavedAddressesContent
          cryptoAddresses={cryptoAddresses}
          onEditCryptoAddress={editCryptoAddress}
          bankAddresses={bankAddresses}
          onEditBankAddress={editBankAddress}
          cards={cards}
          onEditCard={editCard}
        />
      )}
    </ModalComponentWrapper>
  );
}
